import React, { useState, useEffect, useRef } from 'react';
import { Col } from 'reactstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import QuestionItem from './QuestionItem';
import useConfig from '../../../util/useConfig';
import { optionsAvailable } from './utils';
import { partial } from '../../../util/translation/translation';

const QuestionBuilder = ({
  pickedOptions,
  setPickedOptions,
  type,
  questionType,
  showValidAnswerCheckbox,
  onValidAnswerChange,
}) => {
  const [availableTeams, setAvailableTeams] = useState([]);
  const config = useConfig();
  const p = partial('questionaire');
  const radioGroupNameRef = useRef(
    `valid-answer-group-${Math.random().toString(36).substring(2, 9)}`
  );
  useEffect(() => {
    if (config && config.teams?.length > 0 && type?.value) {
      const mappedTeams = config.teams.map((team) => ({
        label: team.name,
        value: team.id,
      }));
      setAvailableTeams(mappedTeams);
    }
  }, [config, type]);
  const removeIndicator = () => {
    document.querySelectorAll('.picked-question-card').forEach((element) => {
      element?.parentNode.classList.remove(
        'prepend-indicator',
        'append-indicator'
      );
    });
  };

  const handleOnDrag = ({ source, destination }) => {
    if (!destination) return;
    removeIndicator();
    const sourceQuestion = pickedOptions[source.index];
    const updatedPickedQuestions = [...pickedOptions];
    updatedPickedQuestions.splice(source.index, 1);
    updatedPickedQuestions.splice(destination.index, 0, sourceQuestion);
    setPickedOptions(updatedPickedQuestions);
  };

  const handleMoveQuestion = (question, direction) => {
    const index = pickedOptions.findIndex((q) => q.id === question.id);
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= pickedOptions.length) return;

    const updatedQuestions = [...pickedOptions];
    [updatedQuestions[index], updatedQuestions[newIndex]] = [
      updatedQuestions[newIndex],
      updatedQuestions[index],
    ];

    setPickedOptions(updatedQuestions);
  };
  const handleAddOption = () => {
    setPickedOptions((prev) => {
      return [
        ...prev,
        {
          id: prev.length,
          type: type?.value,
          value: '',
          ...(type?.value === 'SCORE_PREDICTOR' && {
            isValidAnswer: 0,
          }),
        },
      ].map((item, idx) => ({ ...item, id: idx }));
    });
  };
  const removeQuestion = (index) => {
    const optionsCopy = [...pickedOptions];
    optionsCopy.splice(index, 1);
    setPickedOptions(optionsCopy.map((item, idx) => ({ ...item, id: idx })));
  };
  const editOption = (index, value) => {
    const optionsCopy = [...pickedOptions];
    optionsCopy[index].value = value;
    setPickedOptions(optionsCopy);
  };
  const handleValidAnswerChange = (questionId, checked) => {
    const optionsCopy = [...pickedOptions];
    const questionIndex = optionsCopy.findIndex((q) => q.id === questionId);
    const currentType = questionType || type?.value;
    if (questionIndex !== -1) {
      // Handle different question types based on currentType
      if (currentType === 'SCORE_PREDICTOR') {
        // Numeric input allowed (counter)
        const validAnswerValue = checked === '' ? null : Number(checked);
        optionsCopy[questionIndex] = {
          ...optionsCopy[questionIndex],
          isValidAnswer: validAnswerValue,
        };
      } else if (currentType === 'RADIO_BUTTONS') {
        if (!checked) {
          return; // ignore unchecked event to avoid stale override
        }
        // Only react to the checked=true event
        optionsCopy.forEach((opt, idx) => {
          optionsCopy[idx] = {
            ...opt,
            isValidAnswer: idx === questionIndex,
          };
        });
      } else {
        // CHECKBOX, DROPDOWN etc. – toggle boolean per option
        const validAnswerValue = Boolean(checked);
        optionsCopy[questionIndex] = {
          ...optionsCopy[questionIndex],
          isValidAnswer: validAnswerValue,
        };
      }
      setPickedOptions(optionsCopy);
    }
  };
  return (
    <DragDropContext onDragEnd={handleOnDrag}>
      <Col>
        <Droppable droppableId="pick">
          {(provided) => (
            <div>
              <div
                className="question-answers-list flex-column"
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {pickedOptions.map((q, index) => (
                  <Draggable
                    key={q.id}
                    draggableId={`pick-${q.id}`}
                    index={index}
                  >
                    {(draggableProvided) => (
                      <div
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        {...draggableProvided.dragHandleProps}
                      >
                        <QuestionItem
                          question={q}
                          onDelete={() => removeQuestion(index)}
                          onMoveUp={() => handleMoveQuestion(q, -1)}
                          onMoveDown={() => handleMoveQuestion(q, 1)}
                          handleInput={(value) => editOption(index, value)}
                          type={questionType || type?.value}
                          availableTeams={availableTeams}
                          showValidAnswerCheckbox={showValidAnswerCheckbox}
                          onValidAnswerChange={handleValidAnswerChange}
                          radioGroupName={radioGroupNameRef.current}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
              {optionsAvailable(type?.value) && (
                <div
                  style={{ color: 'blue' }}
                  onClick={handleAddOption}
                  role="button"
                  tabIndex={0}
                >
                  {p('addOption')}
                </div>
              )}
            </div>
          )}
        </Droppable>
      </Col>
    </DragDropContext>
  );
};

export default QuestionBuilder;
